import React, {
  ReactNode,
  useEffect,
  useSyncExternalStore,
  useRef,
  useCallback,
} from 'react'

import cx from 'classnames'
import {
  dark,
  light,
  dark_mode_switch,
} from '../layouts/styles.module.css'

// A safe fallback function for when window isn't available
const emptySubscribe = () => () => { }

export function useMediaQuery(query: string): boolean {
  const isServer = typeof window === 'undefined'

  const subscribe = useCallback(
    (callback: () => void) => {
      if (isServer) return emptySubscribe()

      const matchMedia = window.matchMedia(query)

      if (matchMedia.addEventListener) {
        matchMedia.addEventListener('change', callback)
        return () => matchMedia.removeEventListener('change', callback)
      } else {
        matchMedia.addListener(callback)
        return () => matchMedia.removeListener(callback)
      }
    },
    [query, isServer]
  )

  const getSnapshot = useCallback(() => {
    if (isServer) return true;
    return window.matchMedia(query).matches
  }, [])

  const getServerSnapshot = useCallback(() => {
    if (isServer) return false;
    return getSnapshot();
  }, [isServer, getSnapshot])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}



interface DarkModeSwitchProps {
  children: [ReactNode, ReactNode]; // Enforces exactly two children
}

export const DarkModeSwitch = ({ children: [lightChild, darkChild] }: DarkModeSwitchProps) => {
  const [darkMode] = useToggleDarkMode()
  return (
    <span className={dark_mode_switch}>
      <span style={{ opacity: darkMode ? 0 : 1 }}>{lightChild}</span>
      <span style={{ opacity: darkMode ? 1 : 0 }}>{darkChild}</span>
    </span>
  )
}


export const useToggleDarkMode = (): [boolean, () => void] => {

  const mediaPreference = useMediaQuery('(prefers-color-scheme: dark)')

  const getServerSnapshot = useCallback(() => null, [])

  const getSnapshot = useCallback((): boolean | null => {
    if (typeof window === 'undefined') return null
    const pref = localStorage.getItem('darkModeEnabled')
    if (pref) return pref === 'true'
    return null
  }, [])

  const subscribe = useCallback((callback: () => void) => {
    window.addEventListener('storage', callback)
    return () => window.removeEventListener('storage', callback)
  }, [])

  const localStoragePreference = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const isDarkMode = (localStoragePreference !== null)
    ? localStoragePreference
    : mediaPreference

  useEffect(() => {
    if (typeof window === 'undefined' || !document?.documentElement) return

    const classes = document.documentElement.classList
    const [add, remove] = isDarkMode
      ? [cx(dark), cx(light)]
      : [cx(light), cx(dark)]

    classes.add(add)
    classes.remove(remove)
  }, [isDarkMode])

  const toggleDarkMode = useCallback(() => {
    if (typeof window === 'undefined') return

    const storageValue = localStorage.getItem('darkModeEnabled')
    const matchMedia = window.matchMedia('(prefers-color-scheme: dark)').matches

    // If it's in localStorage, use it. If not, fallback to the system media query!
    const currentMode = storageValue !== null
      ? storageValue === 'true'
      : matchMedia

    const nextMode = !currentMode
    if (nextMode == matchMedia) {
      localStorage.removeItem('darkModeEnabled')
    } else {
      localStorage.setItem('darkModeEnabled', String(nextMode))
    }
    window.dispatchEvent(new Event('storage'))
  }, [])

  return [isDarkMode, toggleDarkMode]
}

export type HoverControllerProps = {
  onPointerEnter: (e: React.PointerEvent) => void
  onPointerLeave: (e: React.PointerEvent) => void
  onClick: (e: React.MouseEvent) => void
}

export const useHoverDarkMode = (): [boolean, HoverControllerProps] => {
  const [isDarkMode, toggleDarkMode] = useToggleDarkMode()

  const isDarkModeRef = useRef(isDarkMode)
  isDarkModeRef.current = isDarkMode

  const hasClickedRef = useRef(false)
  const initialStateRef = useRef(isDarkMode)

  const onPointerEnter = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return

    initialStateRef.current = isDarkModeRef.current
    hasClickedRef.current = false
    toggleDarkMode()
  }, [toggleDarkMode])

  const onPointerLeave = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return
    if (hasClickedRef.current) return
    toggleDarkMode()
  }, [toggleDarkMode])

  const onClick = useCallback((e: React.MouseEvent) => {

    const isMobileTouch = (e.nativeEvent as PointerEvent).pointerType === 'touch' || window.matchMedia('(pointer: coarse)').matches

    if (isMobileTouch || hasClickedRef.current) {
      toggleDarkMode()
    }
    hasClickedRef.current = true
  }, [toggleDarkMode])

  return [
    isDarkMode,
    {
      onPointerEnter,
      onPointerLeave,
      onClick,
    },
  ]
}
